import { Directive, Host, HostListener, Input, Self } from '@angular/core';
import {
  GlobalPositionStrategy,
  Overlay,
  OverlayConfig,
  OverlayRef
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { McRAIImage } from '../models/image';
import { ImageComponent } from '../components/image/image.component';

@Directive({
  selector: '[mcraiImageDetail]',
  host: {
    '[style.cursor]': '"pointer"'
  }
})
export class ImageDetailDirective {
  private _dispose: Subject<null> = new Subject<null>();
  private _overlayRef: OverlayRef;

  private _instance: ImageComponent;

  private _images: McRAIImage[];
  @Input() set mcraiImageDetail(value: McRAIImage | McRAIImage[]) {
    this._images = Array.isArray(value) ? value : [value];
  }

  private _defaultIndex: number;
  @Input() set defaultIndex(value: number) {
    this._defaultIndex = value;
  }

  constructor(
    private _overlay: Overlay,
    @Host() @Self() private _hostImage: ImageComponent
  ) { }

  @HostListener('click') _onClick(): void {
    this.open();
  }

  @HostListener('window:keyup', ['$event']) private _onKeyUp(event): void {
    if (!this._instance) {
      return;
    }

    switch (event.code) {
      case 'Escape':
        this.close();
        return;
      case 'ArrowLeft':
        if (!this._instance.isFirst) {
          this._instance.prev(event);
        }
        return;
      case 'ArrowRight':
        if (!this._instance.isLast) {
          this._instance.next(event);
        }
        return;
    }
  }

  private _getPosition(): GlobalPositionStrategy {
    return this._overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();
  }

  private _getConfig(): OverlayConfig {
    return new OverlayConfig({
      positionStrategy: this._getPosition(),
      hasBackdrop: true,
      width: '90%',
      height: '90%'
    });
  }

  public open(): void {
    const portal = new ComponentPortal(ImageComponent);
    const config = this._getConfig();

    this._overlayRef = this._overlay.create(config);
    this._instance   = this._overlayRef.attach(portal).instance;

    this._instance.data = this._images;
    this._instance.width = '100%';
    this._instance.height = '100%';
    this._instance.activeIndex = this._defaultIndex ? this._defaultIndex : this._hostImage.activeIndex;
    this._instance.showCloseButton = true;
    this._instance.controlColor = 'alternative';

    this._overlayRef.backdropClick()
      .pipe(takeUntil(this._dispose))
      .subscribe(() => this.close());

    this._instance.closeEvent
      .pipe(takeUntil(this._dispose))
      .subscribe(() => this.close());
  }

  public close(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._dispose.next();
    }
  }
}
